import React, { useState, useEffect, useRef, useCallback } from 'react';
import MuxUploader from '@mux/mux-uploader-react';
import api from '../services/api';
import { 
  Upload, Video, CheckCircle, AlertCircle, Loader2, 
  X, Film, Music, Tv, PlayCircle, SkipForward, Image, ImagePlus, Link, HardDrive
} from 'lucide-react';

const VideoUpload = ({ onUploadComplete, onClose }) => {
  // Steps: metadata -> upload (main video) -> trailer_prompt -> trailer_upload -> processing -> complete
  const [step, setStep] = useState('metadata');
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [uploadUrl, setUploadUrl] = useState(null);
  const [trailerUploadUrl, setTrailerUploadUrl] = useState(null);
  const [contentId, setContentId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, complete, error
  const [error, setError] = useState(null);
  const [statusCheckInterval, setStatusCheckInterval] = useState(null);
  const [includeTrailer, setIncludeTrailer] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailError, setThumbnailError] = useState(null);
  const thumbnailInputRef = useRef(null);
  const uploaderRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(''); // For URL upload mode
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genre: [],
    year: new Date().getFullYear(),
    content_type: 'movie',
    is_premium: false,
    is_ppv: false,
    ppv_price: 4.99,
    duration: '',
    cast: '',
    director: '',
    featured: false
  });

  // Handle Mux uploader events
  const handleMuxProgress = useCallback((e) => {
    const progress = e.detail?.percent ?? e.detail ?? 0;
    setUploadProgress(Math.round(progress));
    setUploadStatus('uploading');
  }, []);

  const handleMuxUploadStart = useCallback(() => {
    setUploadProgress(0);
    setUploadStatus('uploading');
  }, []);

  const handleMuxChunkSuccess = useCallback((e) => {
    // Update progress based on chunks uploaded
    const progress = e.detail?.percent ?? uploadProgress;
    setUploadProgress(Math.round(progress));
  }, [uploadProgress]);

  const genres = [
    'Action', 'Adventure', 'Comedy', 'Drama', 'Horror', 'Sci-Fi', 
    'Thriller', 'Mystery', 'Romance', 'Documentary', 'Animation', 
    'Music', 'Fantasy', 'Crime', 'Family'
  ];

  const contentTypes = [
    { value: 'movie', label: 'Movie', icon: Film },
    { value: 'tv_show', label: 'TV Show', icon: Tv },
    { value: 'music_video', label: 'Music Video', icon: Music }
  ];

  const canHaveTrailer = formData.content_type === 'movie' || formData.content_type === 'tv_show';
  
  // Thumbnail aspect ratio requirements
  const getThumbnailRequirements = () => {
    if (formData.content_type === 'music_video') {
      return { ratio: '16:9', width: 1920, height: 1080, label: '16:9 (Landscape)' };
    }
    return { ratio: '9:16', width: 1080, height: 1920, label: '9:16 (Portrait)' };
  };

  const validateThumbnailAspectRatio = (file) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        const { width, height } = img;
        const actualRatio = width / height;
        const requirements = getThumbnailRequirements();
        
        if (formData.content_type === 'music_video') {
          // 16:9 ratio = 1.78
          const expectedRatio = 16 / 9;
          const tolerance = 0.1;
          if (Math.abs(actualRatio - expectedRatio) <= tolerance) {
            resolve({ valid: true, width, height });
          } else {
            reject(`Thumbnail must be 16:9 aspect ratio (landscape) for music videos. Current: ${width}x${height}`);
          }
        } else {
          // 9:16 ratio = 0.5625
          const expectedRatio = 9 / 16;
          const tolerance = 0.1;
          if (Math.abs(actualRatio - expectedRatio) <= tolerance) {
            resolve({ valid: true, width, height });
          } else {
            reject(`Thumbnail must be 9:16 aspect ratio (portrait) for movies/TV shows. Current: ${width}x${height}`);
          }
        }
      };
      img.onerror = () => reject('Failed to load image');
      img.src = URL.createObjectURL(file);
    });
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setThumbnailError(null);
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setThumbnailError('Please select an image file');
      return;
    }
    
    // Validate aspect ratio
    try {
      await validateThumbnailAspectRatio(file);
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    } catch (err) {
      setThumbnailError(err);
      setThumbnailFile(null);
      setThumbnailPreview(null);
    }
  };

  useEffect(() => {
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [statusCheckInterval]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGenreToggle = (genre) => {
    setFormData(prev => ({
      ...prev,
      genre: prev.genre.includes(genre)
        ? prev.genre.filter(g => g !== genre)
        : [...prev.genre, genre]
    }));
  };

  const handleMetadataSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (formData.genre.length === 0) {
      setError('Please select at least one genre');
      return;
    }
    
    if (!thumbnailFile) {
      setError('Thumbnail is required');
      return;
    }
    
    // For URL upload mode, validate URL is provided
    if (uploadMode === 'url' && !videoUrl.trim()) {
      setError('Video URL is required');
      return;
    }

    try {
      setStep('creating');
      
      // First upload thumbnail to get URL
      const thumbnailFormData = new FormData();
      thumbnailFormData.append('file', thumbnailFile);
      
      let thumbnailUrl = '';
      try {
        const thumbResponse = await api.post('/upload/thumbnail', thumbnailFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        thumbnailUrl = thumbResponse.data.url;
      } catch (thumbErr) {
        // If thumbnail upload endpoint doesn't exist, convert to base64
        thumbnailUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(thumbnailFile);
        });
      }
      
      const uploadData = {
        ...formData,
        cast: formData.cast ? formData.cast.split(',').map(c => c.trim()).filter(c => c) : [],
        custom_thumbnail: thumbnailUrl
      };
      
      // Handle URL upload vs file upload
      if (uploadMode === 'url') {
        // URL-based upload - Mux fetches from the URL
        const response = await api.post('/mux/upload-from-url', {
          ...uploadData,
          video_url: videoUrl.trim()
        });
        
        setContentId(response.data.content_id);
        // URL uploads start processing immediately
        startProcessingCheck(response.data.content_id);
      } else {
        // File-based upload - get upload URL for direct upload
        const response = await api.post('/mux/create-upload', uploadData);
        
        setUploadUrl(response.data.upload_url);
        setContentId(response.data.content_id);
        setStep('upload');
      }
      
    } catch (err) {
      console.error('Failed to create upload:', err);
      setError(err.response?.data?.detail || 'Failed to create upload session');
      setStep('metadata');
    }
  };

  const startProcessingCheck = (cId = null) => {
    const checkId = cId || contentId;
    setStep('processing');
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/mux/status/${checkId}`);
        if (response.data.status === 'ready') {
          clearInterval(interval);
          setStatusCheckInterval(null);
          setStep('complete');
          if (onUploadComplete) {
            onUploadComplete(response.data);
          }
        } else if (response.data.status === 'failed' || response.data.status === 'errored') {
          clearInterval(interval);
          setStatusCheckInterval(null);
          setError(response.data.error_message || 'Video processing failed');
          setStep('metadata');
        }
      } catch (err) {
        console.error('Error checking status:', err);
      }
    }, 5000); // Check every 5 seconds
    setStatusCheckInterval(interval);
  };

  const handleMainVideoUploadSuccess = () => {
    // If content type supports trailers, ask if user wants to add one
    if (canHaveTrailer) {
      setStep('trailer_prompt');
    } else {
      startProcessingCheck();
    }
  };

  const handleAddTrailer = async () => {
    try {
      setStep('creating_trailer');
      const response = await api.post(`/mux/create-trailer-upload/${contentId}`);
      setTrailerUploadUrl(response.data.upload_url);
      setStep('trailer_upload');
    } catch (err) {
      console.error('Failed to create trailer upload:', err);
      setError(err.response?.data?.detail || 'Failed to create trailer upload');
      startProcessingCheck(); // Continue without trailer
    }
  };

  const handleSkipTrailer = () => {
    startProcessingCheck();
  };

  const handleTrailerUploadSuccess = () => {
    setIncludeTrailer(true);
    startProcessingCheck();
  };

  // Render the metadata form
  const handleUploadError = (err) => {
    console.error('Upload error:', err);
    setError('Upload failed. Please try again.');
    setStep('metadata');
  };

  const renderMetadataForm = () => (
    <form onSubmit={handleMetadataSubmit} className="space-y-6">
      {/* Upload Mode Toggle */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Upload Method</label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setUploadMode('file')}
            className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-xl border transition-all ${
              uploadMode === 'file'
                ? 'border-electric bg-electric/20 text-electric'
                : 'border-white/20 bg-white/5 text-foreground/60 hover:border-white/40'
            }`}
          >
            <HardDrive className="h-5 w-5" />
            <span>Upload File</span>
          </button>
          <button
            type="button"
            onClick={() => setUploadMode('url')}
            className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-xl border transition-all ${
              uploadMode === 'url'
                ? 'border-electric bg-electric/20 text-electric'
                : 'border-white/20 bg-white/5 text-foreground/60 hover:border-white/40'
            }`}
          >
            <Link className="h-5 w-5" />
            <span>From URL</span>
          </button>
        </div>
        <p className="text-xs text-foreground/50 mt-2">
          {uploadMode === 'url' 
            ? 'Paste a link from Dropbox, Google Drive, or any direct video URL'
            : 'Select a video file from your computer'}
        </p>
      </div>

      {/* Video URL Input (only for URL mode) */}
      {uploadMode === 'url' && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Video URL *</label>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.dropbox.com/s/... or direct video URL"
            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-electric"
          />
          <div className="mt-2 space-y-1">
            <p className="text-xs text-green-400">✓ Recommended: Dropbox, direct .mp4/.mov URLs</p>
            <p className="text-xs text-yellow-400">⚠ Google Drive: May fail for large files due to virus scan restrictions</p>
          </div>
        </div>
      )}

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          placeholder="Enter video title"
          className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-electric"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Enter video description"
          rows={3}
          className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-electric"
        />
      </div>

      {/* Content Type */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Content Type</label>
        <div className="grid grid-cols-3 gap-3">
          {contentTypes.map(type => (
            <button
              key={type.value}
              type="button"
              onClick={() => {
                setFormData(prev => ({ ...prev, content_type: type.value }));
                // Clear thumbnail when content type changes (different aspect ratio)
                setThumbnailFile(null);
                setThumbnailPreview(null);
                setThumbnailError(null);
              }}
              className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-xl border transition-all ${
                formData.content_type === type.value
                  ? 'border-electric bg-electric/20 text-electric'
                  : 'border-white/20 bg-white/5 text-foreground/60 hover:border-white/40'
              }`}
            >
              <type.icon className="h-5 w-5" />
              <span>{type.label}</span>
            </button>
          ))}
        </div>
        {canHaveTrailer && (
          <p className="text-xs text-electric mt-2 flex items-center space-x-1">
            <PlayCircle className="h-3 w-3" />
            <span>You can add an optional trailer after uploading the main video</span>
          </p>
        )}
      </div>

      {/* Thumbnail Upload */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Thumbnail * <span className="text-foreground/60 font-normal">({getThumbnailRequirements().label})</span>
        </label>
        <div className="flex items-start space-x-4">
          {/* Thumbnail Preview */}
          <div 
            className={`relative bg-white/5 border-2 border-dashed rounded-xl overflow-hidden flex items-center justify-center cursor-pointer hover:border-electric/50 transition-colors ${
              thumbnailError ? 'border-red-400' : thumbnailPreview ? 'border-electric' : 'border-white/20'
            }`}
            style={{
              width: formData.content_type === 'music_video' ? '160px' : '90px',
              height: formData.content_type === 'music_video' ? '90px' : '160px'
            }}
            onClick={() => thumbnailInputRef.current?.click()}
          >
            {thumbnailPreview ? (
              <img src={thumbnailPreview} alt="Thumbnail preview" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center p-2">
                <ImagePlus className="h-8 w-8 text-foreground/40 mx-auto mb-1" />
                <span className="text-xs text-foreground/40">Click to upload</span>
              </div>
            )}
          </div>
          <div className="flex-1">
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/*"
              onChange={handleThumbnailChange}
              className="hidden"
            />
            <p className="text-sm text-foreground/60 mb-2">
              {formData.content_type === 'music_video' 
                ? 'Upload a 16:9 landscape thumbnail (e.g., 1920x1080)'
                : 'Upload a 9:16 portrait thumbnail (e.g., 1080x1920)'
              }
            </p>
            {thumbnailFile && (
              <p className="text-sm text-green-400">✓ {thumbnailFile.name}</p>
            )}
            {thumbnailError && (
              <p className="text-sm text-red-400">{thumbnailError}</p>
            )}
          </div>
        </div>
      </div>

      {/* Genres */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Genres *</label>
        <div className="flex flex-wrap gap-2">
          {genres.map(genre => (
            <button
              key={genre}
              type="button"
              onClick={() => handleGenreToggle(genre)}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                formData.genre.includes(genre)
                  ? 'bg-electric text-background'
                  : 'bg-white/5 border border-white/20 text-foreground/60 hover:border-white/40'
              }`}
            >
              {genre}
            </button>
          ))}
        </div>
      </div>

      {/* Year and Duration */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Year</label>
          <input
            type="number"
            name="year"
            value={formData.year}
            onChange={handleInputChange}
            min="1900"
            max="2100"
            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-electric"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Duration</label>
          <input
            type="text"
            name="duration"
            value={formData.duration}
            onChange={handleInputChange}
            placeholder="1h 30m"
            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-electric"
          />
        </div>
      </div>

      {/* Cast and Director */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Cast</label>
          <input
            type="text"
            name="cast"
            value={formData.cast}
            onChange={handleInputChange}
            placeholder="Actor 1, Actor 2"
            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-electric"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Director</label>
          <input
            type="text"
            name="director"
            value={formData.director}
            onChange={handleInputChange}
            placeholder="Director name"
            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-electric"
          />
        </div>
      </div>

      {/* Access Settings */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-foreground mb-2">Access Settings</label>
        <label className="flex items-center space-x-3 cursor-pointer">
          <input type="checkbox" name="featured" checked={formData.featured} onChange={handleInputChange} className="w-5 h-5 rounded border-white/20 bg-white/5 text-electric focus:ring-electric" />
          <span className="text-foreground">Featured on homepage</span>
        </label>
        <label className="flex items-center space-x-3 cursor-pointer">
          <input type="checkbox" name="is_premium" checked={formData.is_premium} onChange={handleInputChange} className="w-5 h-5 rounded border-white/20 bg-white/5 text-electric focus:ring-electric" />
          <span className="text-foreground">Premium content</span>
        </label>
        <label className="flex items-center space-x-3 cursor-pointer">
          <input type="checkbox" name="is_ppv" checked={formData.is_ppv} onChange={handleInputChange} className="w-5 h-5 rounded border-white/20 bg-white/5 text-electric focus:ring-electric" />
          <span className="text-foreground">Pay-Per-View content</span>
        </label>
        {formData.is_ppv && (
          <div className="ml-8">
            <label className="block text-sm text-foreground/60 mb-1">PPV Price ($)</label>
            <input type="number" name="ppv_price" value={formData.ppv_price} onChange={handleInputChange} min="0" step="0.01" className="w-32 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-electric" />
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center space-x-2 text-red-400 bg-red-400/10 px-4 py-3 rounded-xl">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl bg-white/5 border border-white/20 text-foreground hover:bg-white/10 transition-colors">Cancel</button>
        <button type="submit" className="flex items-center space-x-2 px-6 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-colors">
          <span>{uploadMode === 'url' ? 'Start Processing' : 'Next: Upload Video'}</span>
        </button>
      </div>
    </form>
  );

  const renderUploader = (isTrailer = false) => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-foreground mb-2">
          {isTrailer ? 'Upload Trailer' : 'Upload Main Video'}
        </h3>
        <p className="text-foreground/60">{formData.title}</p>
        {isTrailer && (
          <p className="text-sm text-electric mt-1">This trailer will play in the hero section on the homepage</p>
        )}
      </div>

      {/* Upload Progress Bar - Always visible */}
      <div className="space-y-3">
        <div className="flex justify-between items-center text-sm">
          <span className="text-foreground/60 flex items-center gap-2">
            {uploadStatus === 'idle' && 'Select a file to upload'}
            {uploadStatus === 'uploading' && (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            )}
            {uploadStatus === 'complete' && (
              <>
                <CheckCircle className="h-4 w-4 text-green-400" />
                Upload complete!
              </>
            )}
          </span>
          <span className={`font-bold text-lg ${
            uploadStatus === 'complete' ? 'text-green-400' : 
            uploadStatus === 'uploading' ? 'text-electric' : 'text-foreground/40'
          }`}>
            {uploadProgress}%
          </span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-4 overflow-hidden shadow-inner">
          <div 
            className={`h-full rounded-full transition-all duration-500 ease-out ${
              uploadStatus === 'complete' ? 'bg-gradient-to-r from-green-400 to-green-500' : 
              isTrailer ? 'bg-gradient-to-r from-red-400 to-red-500' : 
              'bg-gradient-to-r from-electric to-cyan-400'
            }`} 
            style={{ width: `${uploadProgress}%` }} 
          />
        </div>
        {uploadStatus === 'uploading' && (
          <p className="text-xs text-yellow-400 text-center flex items-center justify-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Please don't close this window while uploading
          </p>
        )}
      </div>

      <div className="bg-white/5 border border-white/20 rounded-xl p-8">
        <MuxUploader
          ref={uploaderRef}
          endpoint={isTrailer ? trailerUploadUrl : uploadUrl}
          onSuccess={() => {
            setUploadStatus('complete');
            setUploadProgress(100);
            if (isTrailer) {
              handleTrailerUploadSuccess();
            } else {
              handleMainVideoUploadSuccess();
            }
          }}
          onError={handleUploadError}
          onUploadStart={handleMuxUploadStart}
          onProgress={handleMuxProgress}
          onChunkSuccess={handleMuxChunkSuccess}
          style={{
            '--button-background-color': isTrailer ? '#FF6B6B' : '#00F5D4',
            '--button-active-background-color': isTrailer ? '#FF5252' : '#00D4B8',
            '--button-hover-background-color': isTrailer ? '#FF5C5C' : '#00E5C8',
            '--progress-bar-fill-color': isTrailer ? '#FF6B6B' : '#00F5D4',
            '--progress-percentage-display': 'none'
          }}
        />
      </div>
    </div>
  );

  const renderTrailerPrompt = () => (
    <div className="text-center space-y-6 py-8">
      <div className="w-20 h-20 rounded-full bg-electric/20 flex items-center justify-center mx-auto">
        <PlayCircle className="h-10 w-10 text-electric" />
      </div>
      <div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Add a Trailer?</h3>
        <p className="text-foreground/60">
          Would you like to upload a trailer for "{formData.title}"?
        </p>
        <p className="text-sm text-foreground/40 mt-2">
          Trailers are played in the hero section on the homepage for featured content.
        </p>
      </div>
      <div className="flex justify-center space-x-4">
        <button
          onClick={handleSkipTrailer}
          className="flex items-center space-x-2 px-6 py-3 rounded-xl bg-white/5 border border-white/20 text-foreground hover:bg-white/10 transition-colors"
        >
          <SkipForward className="h-5 w-5" />
          <span>Skip</span>
        </button>
        <button
          onClick={handleAddTrailer}
          className="flex items-center space-x-2 px-6 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-colors"
        >
          <PlayCircle className="h-5 w-5" />
          <span>Add Trailer</span>
        </button>
      </div>
    </div>
  );

  const renderProcessing = () => (
    <div className="text-center space-y-6 py-12">
      <div className="w-20 h-20 rounded-full bg-electric/20 flex items-center justify-center mx-auto">
        <Loader2 className="h-10 w-10 text-electric animate-spin" />
      </div>
      <div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Processing Video{includeTrailer || trailerUploadUrl ? 's' : ''}</h3>
        <p className="text-foreground/60">
          Your {includeTrailer || trailerUploadUrl ? 'videos are' : 'video is'} being processed by Mux. This may take a few minutes.
        </p>
      </div>
    </div>
  );

  const renderComplete = () => (
    <div className="text-center space-y-6 py-12">
      <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
        <CheckCircle className="h-10 w-10 text-green-400" />
      </div>
      <div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Upload Complete!</h3>
        <p className="text-foreground/60">"{formData.title}" has been successfully uploaded.</p>
        {(includeTrailer || trailerUploadUrl) && (
          <p className="text-sm text-electric mt-2">Trailer included ✓</p>
        )}
      </div>
      <button onClick={onClose} className="px-8 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-colors">Done</button>
    </div>
  );

  const renderError = () => (
    <div className="text-center space-y-6 py-12">
      <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
        <AlertCircle className="h-10 w-10 text-red-400" />
      </div>
      <div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Processing Failed</h3>
        <p className="text-red-400">{error}</p>
      </div>
      <button onClick={() => { setError(null); setStep('metadata'); }} className="px-8 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-colors">Try Again</button>
    </div>
  );

  const renderCreating = (isTrailer = false) => (
    <div className="text-center space-y-6 py-12">
      <Loader2 className="h-12 w-12 text-electric animate-spin mx-auto" />
      <p className="text-foreground/60">Creating {isTrailer ? 'trailer' : ''} upload session...</p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-electric/20 flex items-center justify-center">
              <Upload className="h-5 w-5 text-electric" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Upload New Video</h2>
              <p className="text-sm text-foreground/60">Add content to your streaming platform</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
            <X className="h-5 w-5 text-foreground/60" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'metadata' && renderMetadataForm()}
          {step === 'creating' && renderCreating()}
          {step === 'upload' && renderUploader(false)}
          {step === 'trailer_prompt' && renderTrailerPrompt()}
          {step === 'creating_trailer' && renderCreating(true)}
          {step === 'trailer_upload' && renderUploader(true)}
          {step === 'processing' && renderProcessing()}
          {step === 'complete' && renderComplete()}
          {step === 'error' && renderError()}
        </div>
      </div>
    </div>
  );
};

export default VideoUpload;
